// 📁 hooks/useFullPageScroll.ts
import { useState, useRef, useEffect, useCallback } from "react";
import { useResponsive } from "./useResponsive";

type UseFullPageScrollProps = {
  sectionsCount?: number;
  duration?: number;
};

export function useFullPageScroll({
  sectionsCount = 1,
  duration = 700,
}: UseFullPageScrollProps) {
  const { isDesktop } = useResponsive();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const isAnimating = useRef<boolean>(false);
  const touchStartY = useRef<number>(0);

  const scrollToSection = useCallback(
    (index: number) => {
      if (!isDesktop || isAnimating.current) return;
      if (index < 0 || index >= sectionsCount) return;

      isAnimating.current = true;
      setActiveIndex(index);

      setTimeout(() => {
        isAnimating.current = false;
      }, duration);
    },
    [sectionsCount, isDesktop, duration]
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!isDesktop || isAnimating.current) return;
      e.preventDefault();
      if (e.deltaY > 0) scrollToSection(activeIndex + 1);
      else scrollToSection(activeIndex - 1);
    },
    [activeIndex, scrollToSection, isDesktop]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isDesktop || isAnimating.current) return;
      if (e.key === "ArrowDown") scrollToSection(activeIndex + 1);
      if (e.key === "ArrowUp") scrollToSection(activeIndex - 1);
    },
    [activeIndex, scrollToSection, isDesktop]
  );

  const handleTouchStart = (e: TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchStartY.current - touchEndY;
    if (Math.abs(deltaY) > 50) {
      if (deltaY > 0) scrollToSection(activeIndex + 1);
      else scrollToSection(activeIndex - 1);
    }
  };

  const handleMouseDown = (e: MouseEvent) => {
    // Chỉ bắt đầu kéo nếu là chuột trái
    if (e.button !== 0) return;
    touchStartY.current = e.clientY;
  };

  const handleMouseUp = (e: MouseEvent) => {
    const touchEndY = e.clientY;
    const deltaY = touchStartY.current - touchEndY;

    // Ngưỡng kéo để chuyển trang (50px)
    if (Math.abs(deltaY) > 50) {
      if (deltaY > 0) {
        scrollToSection(activeIndex + 1);
      } else {
        scrollToSection(activeIndex - 1);
      }
    }
  };

  useEffect(() => {
    if (!isDesktop) return;

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleWheel, handleKeyDown, isDesktop]);

  return { activeIndex, scrollToSection, isDesktop };
}
